import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Quick add for the "Related prints" row (snippets/jhilke-cart-discovery.liquid), in the
 * cart drawer and on the cart page. Each card's button carries its variants as JSON; one
 * shared panel under the row shows Size and Finish choices and adds the chosen variant
 * without leaving the cart.
 *
 * Listeners sit on `document` because the drawer's markup is morphed on every cart change;
 * the panel is plain markup that this script fills in, so a morph simply closes it.
 */

const SECOND_OPTION_LABEL = { Product: 'Finish' };

/** @type {WeakMap<HTMLElement, {data: any, trigger: HTMLButtonElement, picks: string[]}>} */
const state = new WeakMap();

function panelFor(trigger) {
  return trigger.closest('.jh-cart-discovery')?.querySelector('[data-jh-qa-panel]') || null;
}

function money(panel, cents) {
  return formatMoney(cents, panel.dataset.moneyFormat || '{{amount}}', panel.dataset.currency || '');
}

function matchVariant(data, picks) {
  return data.v.find((v) => picks.every((pick, i) => v[i + 1] === pick)) || null;
}

function cheapestAvailable(data) {
  const available = data.v.filter((v) => v[4]);
  const pool = available.length ? available : data.v;
  return pool.reduce((low, v) => (v[3] < low[3] ? v : low), pool[0]);
}

function buildOptions(panel, data, picks) {
  const wrap = panel.querySelector('[data-jh-qa-options]');
  wrap.replaceChildren();
  data.o.forEach((name, i) => {
    const values = [...new Set(data.v.map((v) => v[i + 1]).filter((x) => x != null))];
    if (values.length < 2 && i > 0) return;
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'jh-qa__group';
    const legend = document.createElement('legend');
    legend.textContent = SECOND_OPTION_LABEL[name] || name;
    fieldset.append(legend);
    const group = `jh-qa-${panel.dataset.context}-${i}`;
    for (const value of values) {
      const label = document.createElement('label');
      label.className = 'jh-qa__chip';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = group;
      input.value = value;
      input.dataset.index = String(i);
      input.checked = picks[i] === value;
      const text = document.createElement('span');
      text.textContent = value;
      label.append(input, text);
      fieldset.append(label);
    }
    wrap.append(fieldset);
  });
}

function refresh(panel) {
  const s = state.get(panel);
  if (!s) return;
  const variant = matchVariant(s.data, s.picks);
  const add = panel.querySelector('[data-jh-qa-add]');
  const note = panel.querySelector('[data-jh-qa-note]');
  add.removeAttribute('data-added');
  if (!variant || !variant[4]) {
    add.disabled = true;
    add.textContent = 'Add to cart';
    note.textContent = 'This size and finish is not available right now.';
    return;
  }
  add.disabled = false;
  add.textContent = `Add to cart, ${money(panel, variant[3])}`;
  note.textContent = '';
}

function close(panel, focusTrigger) {
  const s = state.get(panel);
  panel.hidden = true;
  if (s?.trigger) {
    s.trigger.setAttribute('aria-expanded', 'false');
    if (focusTrigger && s.trigger.isConnected) s.trigger.focus();
  }
  state.delete(panel);
}

function open(trigger) {
  const panel = panelFor(trigger);
  if (!panel) return;
  let data;
  try {
    data = JSON.parse(trigger.dataset.jhQa);
  } catch {
    return;
  }
  const previous = state.get(panel);
  if (previous?.trigger === trigger && !panel.hidden) {
    close(panel, true);
    return;
  }
  if (previous?.trigger) previous.trigger.setAttribute('aria-expanded', 'false');
  const start = cheapestAvailable(data);
  const picks = data.o.map((_, i) => start[i + 1]);
  state.set(panel, { data, trigger, picks });
  panel.querySelector('[data-jh-qa-title]').textContent = data.t;
  buildOptions(panel, data, picks);
  refresh(panel);
  panel.hidden = false;
  trigger.setAttribute('aria-expanded', 'true');
  const addButton = panel.querySelector('[data-jh-qa-add]');
  const summary = panel.closest('dialog')?.querySelector('.cart-drawer__summary');
  addButton.style.scrollMarginBottom = `${(summary?.offsetHeight || 0) + 12}px`;
  addButton.scrollIntoView({ block: 'end', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  panel.querySelector('input:checked, input')?.focus({ preventScroll: true });
}

function add(panel) {
  const s = state.get(panel);
  if (!s) return;
  const variant = matchVariant(s.data, s.picks);
  if (!variant || !variant[4]) return;
  const button = panel.querySelector('[data-jh-qa-add]');
  const note = panel.querySelector('[data-jh-qa-note]');
  button.disabled = true;
  note.textContent = 'Adding…';

  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  // The cart page (sections/jhilke-cart.liquid) refreshes through assets/jhilke-cart-live.js.
  window.jhCartLive?.sectionIds().forEach((id) => sectionIds.add(id));

  const body = JSON.stringify({
    id: variant[0],
    quantity: 1,
    sections: [...sectionIds].join(','),
    sections_url: window.location.pathname,
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  if (sectionIds.size) {
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ id: String(variant[0]), quantity: 1 }],
        promise: deferred.promise,
      })
    );
  }

  fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', fetchConfig('json', { body }))
    .then((response) => response.json())
    .then((json) => {
      if (json.status || json.errors) throw new Error(json.description || json.message || 'Could not add to cart');
      if (!sectionIds.size) {
        window.location.reload();
        return;
      }
      let itemCount;
      for (const id of sectionIds) {
        const html = json.sections?.[id];
        const countText = html && new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]')?.textContent;
        if (countText) {
          itemCount = parseInt(countText, 10);
          break;
        }
      }
      deferred.resolve({
        cart: undefined,
        detail: { sections: json.sections, items: [json], itemCount, source: 'jhilke-quick-add', didError: false },
      });
      note.textContent = 'Added to your cart.';
      button.setAttribute('data-added', '');
      window.jhCartLive?.apply(json.sections, 'Added to your cart.');
    })
    .catch((error) => {
      if (sectionIds.size) deferred.reject(error);
      document.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Failed to add to cart', code: 'SERVICE_UNAVAILABLE' }));
      note.textContent = 'That did not work. Please try again.';
      button.disabled = false;
    });
}

if (!window.__jhQuickAdd) {
  window.__jhQuickAdd = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const trigger = target.closest('.jh-cart-discovery__qa');
    if (trigger instanceof HTMLButtonElement) {
      event.preventDefault();
      open(trigger);
      return;
    }
    const panel = target.closest('[data-jh-qa-panel]');
    if (!(panel instanceof HTMLElement)) return;
    if (target.closest('[data-jh-qa-close]')) close(panel, true);
    else if (target.closest('[data-jh-qa-add]')) add(panel);
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const panel = input.closest('[data-jh-qa-panel]');
    const s = panel instanceof HTMLElement ? state.get(panel) : null;
    if (!s) return;
    s.picks[Number(input.dataset.index)] = input.value;
    refresh(panel);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const panel = event.target instanceof Element ? event.target.closest('[data-jh-qa-panel]') : null;
    if (panel instanceof HTMLElement && !panel.hidden) {
      event.preventDefault();
      event.stopPropagation();
      close(panel, true);
    }
  }, true);
}
